import { useEffect, useState } from "react";
import api, { inr, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Wallet, BookOpen } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

export default function Suppliers() {
  const nav = useNavigate();
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    name: "", contact_person: "", phone: "", email: "", address: "", gstin: "", opening_balance: 0,
  });

  const load = () => api.get("/suppliers").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const startNew = () => {
    setEditing(null);
    setForm({ name: "", contact_person: "", phone: "", email: "", address: "", gstin: "", opening_balance: 0 });
    setOpen(true);
  };
  const startEdit = (s) => {
    setEditing(s);
    setForm({
      name: s.name, contact_person: s.contact_person || "", phone: s.phone || "",
      email: s.email || "", address: s.address || "", gstin: s.gstin || "",
      opening_balance: s.opening_balance || 0,
    });
    setOpen(true);
  };

  const save = async () => {
    try {
      const payload = { ...form, opening_balance: Number(form.opening_balance) };
      if (editing) await api.put(`/suppliers/${editing.id}`, payload);
      else await api.post("/suppliers", payload);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (s) => {
    if (!window.confirm(`Delete ${s.name}?`)) return;
    try { await api.delete(`/suppliers/${s.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="p-6 sm:p-8" data-testid="suppliers-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading font-extrabold text-3xl text-[#2D2C2A]">Suppliers</h1>
          <p className="text-sm text-[#7A7873] mt-1">Vendor master with GSTIN, opening balance and live ledger balance.</p>
        </div>
        <Button onClick={startNew} className="bg-[#D95D39] hover:bg-[#C24F2C]" data-testid="add-supplier-btn">
          <Plus size={16} className="mr-1.5" /> Add Supplier
        </Button>
      </div>

      <div className="bg-white border border-[#EBE8E2] rounded-lg overflow-x-auto">
        <table className="w-full text-sm min-w-[700px]">
          <thead className="bg-[#F4F2EE] text-[#7A7873] text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-2.5">Name</th>
              <th className="text-left px-4 py-2.5">Contact</th>
              <th className="text-left px-4 py-2.5">Phone</th>
              <th className="text-left px-4 py-2.5">GSTIN</th>
              <th className="text-right px-4 py-2.5">Balance Due</th>
              <th className="px-4 py-2.5"></th>
            </tr>
          </thead>
          <tbody>
            {list.map((s) => (
              <tr key={s.id} className="border-t border-[#EBE8E2]" data-testid={`supplier-row-${s.id}`}>
                <td className="px-4 py-2.5 font-medium text-[#2D2C2A]">{s.name}</td>
                <td className="px-4 py-2.5 text-[#7A7873]">{s.contact_person || "—"}</td>
                <td className="px-4 py-2.5 font-mono-num text-[#7A7873]">{s.phone || "—"}</td>
                <td className="px-4 py-2.5 font-mono-num text-xs">{s.gstin || "—"}</td>
                <td className="px-4 py-2.5 text-right font-mono-num font-semibold" style={{ color: s.balance > 0 ? "#D95D39" : "#6A7F66" }}>
                  {inr(s.balance || 0)}
                </td>
                <td className="px-4 py-2.5 text-right">
                  <Button size="sm" variant="outline" className="h-7 mr-1" onClick={() => nav(`/supplier-ledger?id=${s.id}`)} data-testid={`ledger-${s.id}`}>
                    <BookOpen size={12} className="mr-1" /> Ledger
                  </Button>
                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => startEdit(s)}>
                    <Pencil size={13} />
                  </Button>
                  <Button size="icon" variant="ghost" className="h-7 w-7 text-[#E76F51]" onClick={() => del(s)}>
                    <Trash2 size={13} />
                  </Button>
                </td>
              </tr>
            ))}
            {list.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-[#7A7873]">No suppliers yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-lg">
          <DialogHeader><DialogTitle>{editing ? "Edit Supplier" : "Add Supplier"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="supplier-name" /></div>
              <div><Label>Contact Person</Label><Input value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="supplier-phone" /></div>
              <div><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            </div>
            <div><Label>Address</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>GSTIN</Label><Input value={form.gstin} onChange={(e) => setForm({ ...form, gstin: e.target.value })} data-testid="supplier-gstin" /></div>
              <div><Label>Opening Balance (₹)</Label><Input type="number" value={form.opening_balance} onChange={(e) => setForm({ ...form, opening_balance: e.target.value })} /></div>
            </div>
            <div className="text-xs text-[#7A7873]">Tip: Opening balance is positive if you currently owe this supplier.</div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="bg-[#D95D39] hover:bg-[#C24F2C]" onClick={save} data-testid="save-supplier">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
